import { Component, inject, TemplateRef, viewChild, signal, AfterViewInit, OnInit } from '@angular/core';
import { AsyncPipe, CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Observable, catchError, filter, of, switchMap, take } from 'rxjs';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ActivityState, AdmissionState, OrganizationListItem } from '../../../../../services/organizations/organization.model';
import { OrganizationService } from '../../../../../services/organizations/organization.service';
import { ProtectedContentComponent } from '../../../../../components/organisms/protected-content/protected-content.component';
import { NotificationService } from '../../../../../services/form/notification.service';
import { NavigationService } from '../../../../../services/navigation.service';
import { AuthService } from '../../../../../services/auth/auth.service';
import { UserRole } from '../../../../../services/auth/auth.role';
import { BadgeComponent, ButtonComponent, TableComponent, ColumnConfig, ColumnType } from "htx-ui-library";

@Component({
  selector: 'app-organization-overview-page',
  imports: [CommonModule, AsyncPipe, ProtectedContentComponent, ButtonComponent, TableComponent, BadgeComponent, TranslocoPipe],
  templateUrl: './organization-overview-page.component.html',
  styleUrl: './organization-overview-page.component.scss'
})
export class OrganizationOverviewPageComponent implements OnInit, AfterViewInit {
  private router = inject(Router);
  private organizationService = inject(OrganizationService);
  private translocoService = inject(TranslocoService);
  private notificationService = inject(NotificationService);
  private navigationService = inject(NavigationService);
  private authService = inject(AuthService);

  requiredRoles = this.navigationService.getAccessRoles('admin/organizations');

  /** Whether the current user can edit organizations */
  canEditOrganizations = this.navigationService.getAccessRoles('admin/organizations/edit')
    .some(role => this.authService.userRoles$.value.includes(role));

  activityStateTemplateRef = viewChild<TemplateRef<OrganizationListItem>>('columnActivityState');
  admissionStateTemplateRef = viewChild<TemplateRef<OrganizationListItem>>('columnAdmissionState');
  actionsTemplateRef = viewChild<TemplateRef<OrganizationListItem>>('columnActions');

  private activityStateTemplate = signal<TemplateRef<OrganizationListItem> | undefined>(undefined);
  private admissionStateTemplate = signal<TemplateRef<OrganizationListItem> | undefined>(undefined);
  private actionsTemplate = signal<TemplateRef<OrganizationListItem> | undefined>(undefined);
  private templatesReady = signal(false);

  /** Only load all organizations if the user is a DataspaceAuthority; OrgAdmins/Representatives get redirected in ngOnInit */
  data$: Observable<OrganizationListItem[]> = this.authService.userRoles$.pipe(
    filter(roles => roles.length > 0),
    take(1),
    switchMap(roles => {
      const isOrgMember = (roles.includes(UserRole.OrganizationAdmin) || roles.includes(UserRole.OrganizationRepresentative))
        && !roles.includes(UserRole.DataspaceAuthority);
      return isOrgMember
        ? of([])
        : this.organizationService.findAllOrganizations().pipe(
            catchError(() => {
              this.notificationService.showError('organization.overview.loadingError', 5000);
              return of([]);
            })
          );
    })
  );

  columnConfig$: Observable<ColumnConfig<OrganizationListItem>[]> = this.translocoService.selectTranslate([]).pipe(
    switchMap(() => {
      // Wait for templates to be ready before building config
      return new Observable<ColumnConfig<OrganizationListItem>[]>(observer => {
        const checkTemplates = () => {
          if (this.templatesReady()) {
            observer.next(this.buildColumnConfig());
            observer.complete();
          } else {
            setTimeout(checkTemplates, 50);
          }
        };
        checkTemplates();
      });
    })
  );

  private buildColumnConfig(): ColumnConfig<OrganizationListItem>[] {
    const activityStateTmpl = this.activityStateTemplate();
    const admissionStateTmpl = this.admissionStateTemplate();
    const actionsTmpl = this.actionsTemplate();

    return [
      {
        name: 'bpnl',
        columnType: ColumnType.PLAIN,
        headerLabel: this.translocoService.translate('organization.control.bpnl.label'),
        cellContent: (rowData) => rowData.bpnl,
      },
      {
        name: 'legalName',
        columnType: ColumnType.PLAIN,
        headerLabel: this.translocoService.translate('organization.control.partner.label'),
        cellContent: (rowData) => rowData.legalName,
      },
      {
        name: 'createdDate',
        columnType: ColumnType.PLAIN,
        headerLabel: this.translocoService.translate('organization.control.registrationDate.label'),
        cellContent: (rowData) => rowData.createdDate ? new Date(rowData.createdDate).toLocaleDateString('de-DE') : '-',
      },
      {
        name: 'admissionState',
        columnType: ColumnType.TEMPLATE,
        headerLabel: this.translocoService.translate('organization.control.admissionState.label'),
        template: admissionStateTmpl as any,
      },
      {
        name: 'activityState',
        columnType: ColumnType.TEMPLATE,
        headerLabel: this.translocoService.translate('organization.control.activityState.label'),
        template: activityStateTmpl as any,
      },
      {
        name: 'action',
        columnType: ColumnType.TEMPLATE,
        headerLabel: this.translocoService.translate('organization.control.action.label'),
        template: actionsTmpl as any
      }
    ];
  }

  ngOnInit(): void {
    // Wait for roles to be loaded, then redirect OrgAdmins
    this.authService.userRoles$.pipe(
      filter(roles => roles.length > 0),
      take(1)
    ).subscribe(userRoles => {
      const isOrgMember = (userRoles.includes(UserRole.OrganizationAdmin) || userRoles.includes(UserRole.OrganizationRepresentative))
        && !userRoles.includes(UserRole.DataspaceAuthority);

      if (isOrgMember) {
        // Use /organizations/me endpoint to get the current user's organization and redirect
        this.organizationService.getMyOrganization()
          .pipe(take(1))
          .subscribe({
            next: (organization) => {
              if (organization?.id) {
                this.router.navigate(['admin', 'organizations', 'details', organization.id]);
              }
            },
            error: () => {
              this.notificationService.showError('organization.overview.loadingError', 5000);
            }
          });
      }
    });
  }

  ngAfterViewInit(): void {
    this.admissionStateTemplate.set(this.admissionStateTemplateRef());
    this.activityStateTemplate.set(this.activityStateTemplateRef());
    this.actionsTemplate.set(this.actionsTemplateRef());
    // Signal that templates are ready
    this.templatesReady.set(true);
  }

  onRowClick(data: OrganizationListItem): void {
    this.goToDetails(data);
  }

  goToDetails(data: OrganizationListItem): void {
    this.router.navigate(['admin', 'organizations', 'details', data.id]);
  }

  edit(data: OrganizationListItem): void {
    this.router.navigate(['admin', 'organizations', 'details', data.id, 'edit']);
  }

  getTyped(row: unknown) {
    return row as OrganizationListItem;
  }

  protected readonly AdmissionState = AdmissionState;
  protected readonly ActivityState = ActivityState;
}
